import { reglagesProfilsSchema, type ReglagesProfils } from '@artosera/shared';
import { Button, Input } from '@artosera/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { profilsApi, useGesteProfil, useReglagesProfils } from '../../api/profils.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { GardeSaisie } from '../GardeSaisie.js';
import { RetourGeste } from '../RetourGeste.js';
import page from './ReglagesPage.module.css';
import styles from '../profils/Profils.module.css';

export function ProfilsPage() {
  const { galerie } = useShellContext();
  if (
    !galerieAModule(galerie, 'profils-collectionneurs') ||
    !galerieALaction(galerie, 'profils:read')
  )
    return null;
  return <Reglages />;
}

function Reglages() {
  const { t } = useTranslation('regie');
  const { galerieSlug, galerie } = useShellContext();
  const reglages = useReglagesProfils(galerieSlug);
  const peutEcrire =
    galerieALaction(galerie, 'profils:write') && galerieALaction(galerie, 'settings:write');
  return (
    <div className={`${page.page} ${styles.contenu}`}>
      <h1 className={page.titre}>{t('profils.reglages.titre')}</h1>
      <p>{t('profils.reglages.formule')}</p>
      {reglages.isLoading && <p role="status">{t('commun:chargement')}</p>}
      {reglages.isError && <p role="alert">{t('commun:erreurReseau')}</p>}
      {reglages.data && <FormulaireReglages donnees={reglages.data} peutEcrire={peutEcrire} />}
      <section className={styles.section}>
        <h2>{t('profils.reglages.informationTitre')}</h2>
        <p>{t('profils.reglages.information')}</p>
        <p>{t('profils.reglages.juriste')}</p>
      </section>
    </div>
  );
}

function FormulaireReglages({
  donnees,
  peutEcrire,
}: {
  donnees: ReglagesProfils;
  peutEcrire: boolean;
}) {
  const { t } = useTranslation('regie');
  const { galerieSlug } = useShellContext();
  const [seuils, setSeuils] = useState({
    petit: String(donnees.seuilsFormatCm.petit),
    moyen: String(donnees.seuilsFormatCm.moyen),
    grand: String(donnees.seuilsFormatCm.grand),
  });
  const [demiVie, setDemiVie] = useState(String(donnees.demiVieJours));
  const saisie = JSON.stringify([seuils, demiVie]);
  const [enregistre, setEnregistre] = useState(saisie);
  const [erreur, setErreur] = useState<string | null>(null);
  const [retour, setRetour] = useState<string | null>(null);
  const modifier = useGesteProfil(galerieSlug, (corps: ReglagesProfils, appel) =>
    profilsApi.modifierReglages(galerieSlug, corps, appel),
  );
  async function enregistrer() {
    setErreur(null);
    setRetour(null);
    const validation = reglagesProfilsSchema.safeParse({
      seuilsFormatCm: {
        petit: Number(seuils.petit),
        moyen: Number(seuils.moyen),
        grand: Number(seuils.grand),
      },
      demiVieJours: Number(demiVie),
    });
    if (!validation.success) {
      setErreur(t('profils.reglages.invalide'));
      return;
    }
    try {
      await modifier.mutateAsync(validation.data);
      setEnregistre(saisie);
      setRetour(t('profils.enregistre'));
    } catch (cause) {
      setErreur(estApiError(cause) ? cause.message : t('commun:erreurReseau'));
    }
  }
  if (!peutEcrire)
    return (
      <dl className={styles.donnees}>
        {(['petit', 'moyen', 'grand'] as const).map((format) => (
          <div key={format}>
            <dt>{t('profils.reglages.seuil', { format: t(`profils.formats.${format}`) })}</dt>
            <dd>{donnees.seuilsFormatCm[format]} cm</dd>
          </div>
        ))}
        <div>
          <dt>{t('profils.reglages.demiVie')}</dt>
          <dd>{donnees.demiVieJours}</dd>
        </div>
      </dl>
    );
  return (
    <form
      className={styles.formulaire}
      onSubmit={(event) => {
        event.preventDefault();
        void enregistrer();
      }}
    >
      <GardeSaisie actif={saisie !== enregistre} />
      {(['petit', 'moyen', 'grand'] as const).map((format) => (
        <Field
          key={format}
          htmlFor={`profils-seuil-${format}`}
          label={t('profils.reglages.seuil', { format: t(`profils.formats.${format}`) })}
        >
          <Input
            id={`profils-seuil-${format}`}
            type="number"
            min={0}
            step="any"
            required
            value={seuils[format]}
            onChange={(event) => {
              setSeuils({ ...seuils, [format]: event.target.value });
              setRetour(null);
            }}
          />
        </Field>
      ))}
      <Field htmlFor="profils-demi-vie" label={t('profils.reglages.demiVie')}>
        <Input
          id="profils-demi-vie"
          type="number"
          min={7}
          max={730}
          step={1}
          required
          value={demiVie}
          onChange={(event) => {
            setDemiVie(event.target.value);
            setRetour(null);
          }}
        />
      </Field>
      {erreur && <p role="alert">{erreur}</p>}
      <RetourGeste message={retour} />
      <Button type="submit" loading={modifier.isPending}>
        {t('commun:actions.enregistrer')}
      </Button>
    </form>
  );
}
